import { useMemo, useContext } from 'react'
import * as React from 'react'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'

import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import ContactContext from 'contexts/contactContext'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import BarCodeComponent from 'components/Pages/ExperienceVoucher/BarCode'
import { LUX_LOGO_URL } from 'components/Common/QRCode'
import ExperiencesQRCode from 'components/Pages/ExperienceVoucher/ExperiencesQRCode'
const PolicyDivider = styled.hr`
  grid-column: span 2;
  width: 100%;
  height: 0;
  margin: 0;
  border: none;
  border-top: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
`

const ExperienceItemRow = styled.div`
  margin-left: ${rem(8)};
`

const SubjectToAvailabilityText = styled(BodyText)`
  margin-top: ${rem(12)};
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(16)} ${rem(40)};
`

const InfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

const PinWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${rem(4)};
`

const PolicyGrid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  grid-row-gap: ${rem(12)};
  grid-column-gap: ${rem(16)};
  margin-top: ${rem(8)};
`

const TitleText = styled.span`
  font-style: ${props => props.theme.font.secondaryAlternate.fontStyle};
  font-family: ${props => props.theme.font.secondaryAlternate.family};
  font-weight: 450;
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(20)};
  gap: ${rem(16)};
`

const VoucherDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  margin-top: ${rem(16)};
  @media print {
    > * {
      break-inside: avoid;
    }
  }
`

const HeaderTitle = styled.span`
  font-size: ${rem(24)};
  line-height: 1.3;
  font-weight: 450;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const VendorInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(12)};
  svg {
    margin-top: ${rem(2)};
  }
`

const QRCodeWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(8)};
  margin-top: ${rem(6)};
`

const VoucherCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${rem(32)} ${rem(24)};
  overflow-y: auto;
  @media print {
    padding: ${rem(24)} ${rem(20)};
    overflow-y: visible;
  }
`

const HeaderBanner = styled.div`
  display: flex;
  justify-content: space-between;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  @media print {
    background: ${props => props.theme.palette.neutral.surface.lightBackground} !important;
  }
`

const InclusionsCard = styled.div`
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(4)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const ContentSection = styled.div`
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(4)} ${rem(4)} 0 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const RedeemSection = styled.div`
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: 0 0 ${rem(4)} ${rem(4)};
  border-top: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(32)};
`

const PhoneButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(16)};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: ${rem(4)};
  text-decoration: none;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  font-size: ${rem(14)};
  font-weight: bold;
  @media print {
    &::after {
      content: '' !important;
    }
  }
`

const HeaderImageContainer = styled.div`
  width: 45%;
  max-width: ${rem(277)};
  aspect-ratio: 277 / 152;
  overflow: hidden;
  border-top-left-radius: ${rem(100)};
  border-bottom-left-radius: ${rem(100)};
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(20)};
  margin-top: ${rem(24)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  @media print {
    background: ${props => props.theme.palette.neutral.surface.lightBackground} !important;
    break-inside: avoid;
  }
`

const VoucherCardContainer = styled(Pane)`
  width: 100%;
  max-width: ${rem(600)};
  aspect-ratio: 2 / 3;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 ${rem(4)} ${rem(16)} rgba(0, 0, 0, 0.1);
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  @media print {
    max-width: 100%;
    aspect-ratio: auto;
    height: auto;
    overflow: visible;
    box-shadow: none;
    border: none !important;
  }
`

const RedeemMarkdownContent = styled.div`
  ol {
    list-style: none;
    padding-left: 0;
    counter-reset: step;
    display: flex;
    flex-direction: column;
    gap: ${rem(8)};
    margin: ${rem(8)} 0 0;
  }
  ol li {
    display: flex;
    align-items: center;
    gap: ${rem(8)};
    counter-increment: step;
    &::before {
      content: counter(step);
      min-width: ${rem(20)};
      width: ${rem(20)};
      height: ${rem(20)};
      border-radius: 50%;
      background: ${props => props.theme.palette.neutral.surface.darkBackground};
      color: ${props => props.theme.palette.neutral.surface.darkContrast};
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: ${rem(12)};
      font-weight: bold;
      flex-shrink: 0;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
  @media print {
    ol li::before {
      background: ${props => props.theme.palette.neutral.surface.darkBackground} !important;
      color: ${props => props.theme.palette.neutral.surface.darkContrast} !important;
    }
  }
`

interface Props {
  voucher: App.ExperienceVoucher
  experienceOffer?: App.ExperienceOffer
  address: string
  expiryDate: string
  hasExpired: boolean
  date: string | null
  time: string | null
  groupedExperienceItems?: Array<[string, { name: string; count: number }]>
}

export function PrintStandardVoucher(props: Props) {
  const { voucher, experienceOffer, address, expiryDate, hasExpired, date, time, groupedExperienceItems } = props

  const intl = useIntl()
  const supportContact = useContext(ContactContext)

  const barcodeInfo = useMemo(() => {
    const type = voucher.offer.barcodeType
    const isCodeWithPin = type === 'CODE128 + pin'
    const parts = isCodeWithPin ? voucher.voucherCode.split('-') : [voucher.voucherCode]
    return {
      type: isCodeWithPin ? 'CODE128' : type,
      number: parts[0],
      pin: parts[1] ?? undefined,
    }
  }, [voucher])

  const qrCodeUrl = useMemo(() => {
    if (!voucher.qrCodeUrl) {
      const codeRequirements = voucher.offer.voucherCodeRequirements
      const showOnlyVoucherValue = voucher.offer.showOnlyVoucherValue
      const useExistingCodes = 'Use Existing Codes - Vendor provides us with codes to upload'

      if (codeRequirements === useExistingCodes || showOnlyVoucherValue) {
        return barcodeInfo.number
      }
      return `${config.LED_HOST}/login/r/${barcodeInfo.number}`
    }
    return voucher.qrCodeUrl
  }, [voucher, barcodeInfo])

  const cancellationPolicies = useMemo((): Array<[string, Array<App.ExperienceItemRefundPolicy>]> => {
    const refundPolicies = experienceOffer?.copy?.cancellationInfo?.refundPolicies ?? []
    const policyMap: Record<string, Array<App.ExperienceItemRefundPolicy>> = {}
    for (const policy of refundPolicies) {
      const key = String(policy.groupId ?? 1)
      if (!policyMap[key]) policyMap[key] = []
      policyMap[key].push(policy)
    }
    return Object.entries(policyMap)
  }, [experienceOffer?.copy?.cancellationInfo?.refundPolicies])

  const nonRefundableLabel = useMemo(() => {
    const groupedPolicies: Array<App.ExperienceItemRefundPolicy> = cancellationPolicies.flatMap((entry) => entry[1])
    const nonRefundablePolicy = groupedPolicies.reduce<App.ExperienceItemRefundPolicy | undefined>((acc, policy) => {
      if (policy.id === 'BEFORE-BOOKING-DATE') {
        if (!acc) return policy
        return moment.duration(policy.periods[0]).asMilliseconds() < moment.duration(acc.periods[0]).asMilliseconds() ? policy : acc
      }
      return acc
    }, undefined)

    if (!nonRefundablePolicy) {
      return intl.formatMessage({
        id: 'PN6fnNJo9nszr6VfiGHe',
        defaultMessage: 'Outside of other cancellation policies',
        description: 'Default non-refundable label when no specific policy period exists',
      })
    }

    const duration = moment.duration(nonRefundablePolicy.periods[0])
    const hours = duration.asHours()
    const humanizeLabel = hours > 72
      ? intl.formatMessage(
          {
            id: '2YO1dYMd3hjX0BomgMqk',
            defaultMessage: '{count, plural, one {# day} other {# days}}',
            description: 'Day count for non-refundable cancellation period on experience voucher',
          },
          { count: duration.asDays() },
        )
      : intl.formatMessage(
          {
            id: 'SWwrJqYccKjfvgwZl6n4',
            defaultMessage: '{count, plural, one {# hour} other {# hours}}',
            description: 'Hour count for non-refundable cancellation period on experience voucher',
          },
          { count: hours },
        )

    return intl.formatMessage(
      {
        id: 'gZKVHo8b9XeWsUkzHUDh',
        defaultMessage: 'From {label} before the booking date',
        description: 'Non-refundable period label in experience cancellation policy modal',
      },
      { label: humanizeLabel },
    )
  }, [cancellationPolicies, intl])

  const addressLabel = intl.formatMessage({
    id: '6js6ISlsGTasE8VOV2-p',
    defaultMessage: 'Address',
    description: 'Form field label for vendor address on experience voucher',
  })

  const voucherCodeLabel = intl.formatMessage({
    id: 'yDGpvR2jIVFBksGr9rOA',
    defaultMessage: 'Voucher Code:',
    description: 'Label for the voucher code field on the gift card confirmation or details display',
  })

  const inclusionsLabel = intl.formatMessage({
    id: 'AU0IHG5ev5c1kiUNLtf3',
    defaultMessage: 'Inclusions',
    description: 'Section title for the list of inclusions provided with the offer or package deal',
  })

  const phoneLabel = intl.formatMessage({
    id: 'RkEJm_yFkmMcKqJKxTzA',
    defaultMessage: 'Phone',
    description: 'Label for vendor phone number on experience voucher',
  })

  const expiresLabel = intl.formatMessage({
    id: 'PixYcs2YlPNVpFG_ArIv',
    defaultMessage: 'Expires:',
    description: 'Label for expiration date on experience gift print page',
  })

  const howToRedeemLabel = intl.formatMessage({
    id: 'KxJjWZY6IaZYw_uEw3z9',
    defaultMessage: 'How to redeem your experience',
    description: 'Section title for how to redeem instructions for the experience',
  })

  const expiredLabel = intl.formatMessage({
    id: 'nweyNgO5WQyDb-dNlhUf',
    defaultMessage: 'EXPIRED',
    description: 'Status label when experience voucher has expired in the experiences section',
  })

  const subjectToAvailabilityLabel = intl.formatMessage({
    id: '1QqGqacj7UULzqK-7jQw',
    defaultMessage: '*Subject to availability and seasonal surcharges.',
    description: 'Disclaimer about availability and surcharges for gift offer',
  })

  const pinLabel = intl.formatMessage({
    id: 'GzORz4ohnnBItv39Bltx',
    defaultMessage: 'PIN:',
    description: 'Label for the voucher PIN code for promotional codes or vouchers',
  })

  const conditionsLabel = intl.formatMessage({
    id: '6GH9-gFp3TPH5G9ztxKn',
    defaultMessage: 'Conditions of the Deal',
    description: 'Section title for experience voucher deal conditions',
  })

  const cancellationInfoLabel = intl.formatMessage({
    id: 'gZzDhhNMMK-YYftGXgdw',
    defaultMessage: 'Cancellation Policy',
    description: 'Section title for cancellation info on experience voucher',
  })

  const dateLabel = intl.formatMessage({
    id: 'I9Yh6owNTO-5gtxo9pXt',
    defaultMessage: 'Date',
    description: 'Form field label for date selection in experience booking',
  })

  const timeLabel = intl.formatMessage({
    id: 'DY0hkgkIQ1cGqrnGW0eC',
    defaultMessage: 'Time',
    description: 'Label for time slot selection in experience booking',
  })

  const isLuxBrand = useMemo(() => {
    return config.brandConfig?.subscriptionParentBrand === 'luxuryescapes'
  }, [])

  return (
    <VoucherCardContainer border="light-foreground" borderRadius="M">
      <VoucherCardContent>
        <HeaderBanner>
          <HeaderContent>
            <LogoIcon
              mobileVariant="icon+text"
              tabletVariant="icon+text"
              desktopVariant="icon+text"
              height={24}
              width={125}
            />
            <HeaderTitle>
              <FormattedMessage
                id="-GTeLDsy8ExhEmTKhmwl"
                defaultMessage="Your <italic>experience</italic>{br}voucher"
                description="Header title on the experience voucher page for the experience"
                values={{
                  italic: (chunks: React.ReactNode) => <TitleText>{chunks}</TitleText>,
                  br: <br />,
                }}
              />
            </HeaderTitle>
          </HeaderContent>
          {experienceOffer?.images?.[0] && (
            <HeaderImageContainer>
              <Image
                image={experienceOffer.images[0]}
                width={277}
                height={152}
                fit="center"
              />
            </HeaderImageContainer>
          )}
        </HeaderBanner>
        <VoucherDetailsSection>
          <div>
            <ContentSection>
              <Group direction="horizontal" horizontalAlign="space-between" fullWidth>
                <Group direction="vertical" gap={4}>
                  <BodyText variant="small" format="uppercase">
                    {voucher.location.name}
                  </BodyText>
                  <BodyText variant="medium" weight="bold">
                    {voucher.offer.vendorName}
                  </BodyText>
                  {groupedExperienceItems && groupedExperienceItems.length > 0 && (
                    <div>
                      {groupedExperienceItems.map(([fareType, { name, count }]) => (
                        <ExperienceItemRow key={fareType}>
                          <BodyText variant="small">
                            {intl.formatMessage(
                              {
                                id: 'arnIDxxmbtqG5jJUkF60',
                                defaultMessage: '• {count, number}x {name}',
                                description: 'Experience item showing count and name, e.g. • 2x Snorkelling Tour',
                              },
                              { count, name },
                            )}
                          </BodyText>
                        </ExperienceItemRow>
                      ))}
                    </div>
                  )}
                  {(!groupedExperienceItems || groupedExperienceItems.length === 0) && (
                    <Group direction="vertical" gap={2}>
                      <BodyText variant="large" weight="bold">
                        {voucher.offerPackage.name}
                      </BodyText>
                      {voucher.offerPackage.description && (
                        <BodyText variant="small">
                          {voucher.offerPackage.description}
                        </BodyText>
                      )}
                    </Group>
                  )}
                </Group>
                <QRCodeWrapper>
                  {barcodeInfo.type === 'QR_CODE' && (
                    <ExperiencesQRCode
                      value={qrCodeUrl}
                      size={110}
                      logo={voucher.qrCodeLogoUrl ? voucher.qrCodeLogoUrl : isLuxBrand ? LUX_LOGO_URL : undefined}
                    />
                  )}
                  {!!barcodeInfo.type && ['CODE39', 'CODE128'].includes(barcodeInfo.type) && (
                    <BarCodeComponent
                      width={1}
                      height={50}
                      format={barcodeInfo.type}
                      displayValue={false}
                      value={barcodeInfo.number}
                    />
                  )}
                  {barcodeInfo.pin && (
                    <PinWrapper>
                      <BodyText variant="small">{pinLabel} </BodyText>
                      <BodyText weight="bold" variant="medium">
                        {barcodeInfo.pin}
                      </BodyText>
                    </PinWrapper>
                  )}
                </QRCodeWrapper>
              </Group>
              <InfoGrid>
                {date && (
                  <InfoCell>
                    <BodyText weight="bold" variant="small">{dateLabel}</BodyText>
                    <BodyText variant="small" weight="bold">{date}</BodyText>
                  </InfoCell>
                )}
                {time && (
                  <InfoCell>
                    <BodyText variant="small" weight="bold">{timeLabel}</BodyText>
                    <BodyText variant="small" weight="bold" wrap="letter-wrap">
                      {time}
                    </BodyText>
                  </InfoCell>
                )}
                <InfoCell>
                  {hasExpired && <BodyText weight="bold" colour="critical" variant="small">{expiredLabel}</BodyText>}
                  {!hasExpired && <BodyText variant="small" weight="bold">{expiresLabel}</BodyText>}
                  <BodyText variant="small" weight="bold">{expiryDate}</BodyText>
                </InfoCell>
                <InfoCell>
                  <BodyText variant="small" weight="bold">{voucherCodeLabel}</BodyText>
                  <BodyText variant="small" weight="bold" wrap="letter-wrap">
                    {voucher.voucherCode}
                  </BodyText>
                </InfoCell>
              </InfoGrid>
              <SubjectToAvailabilityText variant="small" colour="light-foreground-alt">
                {subjectToAvailabilityLabel}
              </SubjectToAvailabilityText>
            </ContentSection>
            <RedeemSection>
              {voucher.offer.howToRedeem && (
                <Group direction="vertical" gap={8}>
                  <BodyText variant="medium" weight="bold">
                    {howToRedeemLabel}
                  </BodyText>
                  <RedeemMarkdownContent>
                    <MarkdownRender
                      content={voucher.offer.howToRedeem}
                      fontSize="small"
                    />
                  </RedeemMarkdownContent>
                </Group>
              )}
              <Group direction="vertical" gap={12}>
                <BodyText variant="medium" weight="bold">
                  {voucher.offer.vendorName}
                </BodyText>
                {(voucher.location.phone || experienceOffer?.vendor?.bookingPhone) && (
                  <VendorInfoRow>
                    <LinePhoneIcon />
                    <Group direction="vertical" gap={2}>
                      <BodyText variant="small" weight="bold">{phoneLabel}</BodyText>
                      <BodyText variant="small">
                        {voucher.location.phone || experienceOffer?.vendor?.bookingPhone}
                      </BodyText>
                    </Group>
                  </VendorInfoRow>
                )}
                <VendorInfoRow>
                  <LineMapMarkerIcon />
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="small" weight="bold">{addressLabel}</BodyText>
                    <BodyText variant="small">{address}</BodyText>
                  </Group>
                </VendorInfoRow>
              </Group>
            </RedeemSection>
          </div>
          {voucher.offer.inclusions && (
            <InclusionsCard>
              <BodyText variant="medium" weight="bold">
                {inclusionsLabel}
              </BodyText>
              <MarkdownRender
                content={voucher.offer.inclusions}
                fontSize="small"
              />
            </InclusionsCard>
          )}
          {experienceOffer?.copy?.cancellationInfo && (
            <Group direction="vertical" gap={8}>
              <BodyText variant="medium" weight="bold">
                {cancellationInfoLabel}
              </BodyText>
              <BodyText variant="small">
                {experienceOffer.copy.cancellationInfo.text}
              </BodyText>
              {cancellationPolicies.length > 0 && (
                <PolicyGrid>
                  <BodyText variant="small" weight="bold">
                    {intl.formatMessage({
                      id: 'Sw-S1Pmbt8v8zp5DUw3f',
                      defaultMessage: 'Cancel by',
                      description: 'Column header for cancellation period in voucher policy grid',
                    })}
                  </BodyText>
                  <BodyText variant="small" weight="bold">
                    {intl.formatMessage({
                      id: 'rNvM7dQPditA2Yis2ixs',
                      defaultMessage: 'Charge',
                      description: 'Column header for charge in voucher policy grid in the experiences section',
                    })}
                  </BodyText>
                  <PolicyDivider />
                  {cancellationPolicies.map(([_, groupPolicies], index) => {
                    const refundValue = Math.min(...groupPolicies.map((policy) => policy.value))
                    return (
                      <React.Fragment key={index}>
                        <Group direction="vertical" gap={4}>
                          {groupPolicies.map((policy) => (
                            <BodyText variant="small" weight="bold" key={policy.id}>
                              {policy.periodLabel}
                            </BodyText>
                          ))}
                        </Group>
                        <Group direction="vertical" verticalAlign="center">
                          {refundValue > 0 && (
                            <BodyText variant="small">
                              {intl.formatMessage(
                                {
                                  id: 'YJmCVvx-VaJ_-St831rr',
                                  defaultMessage: '{refundValue}% refund to your original payment method or credits.',
                                  description: 'Refund percentage text in experience cancellation policy modal',
                                },
                                { refundValue },
                              )}
                            </BodyText>
                          )}
                          {refundValue === 0 && (
                            <BodyText variant="small">
                              {intl.formatMessage({
                                id: 'IUAYulNzuNu8pyqZKiwt',
                                defaultMessage: 'Non-refundable',
                                description: 'Label for non-refundable policy in experience cancellation modal',
                              })}
                            </BodyText>
                          )}
                        </Group>
                        <PolicyDivider />
                      </React.Fragment>
                    )
                  })}
                  <BodyText variant="small" weight="bold">
                    {nonRefundableLabel}
                  </BodyText>
                  <Group direction="vertical" verticalAlign="center">
                    <BodyText variant="small">
                      {intl.formatMessage({
                        id: 'a7WB5ghrC1IQzeo4_zqZ',
                        defaultMessage: 'Non-refundable',
                        description: 'Default non-refundable label in experience cancellation modal',
                      })}
                    </BodyText>
                  </Group>
                </PolicyGrid>
              )}
            </Group>
          )}
          {voucher.offer.conditions && (
            <Group direction="vertical" gap={8}>
              <BodyText variant="medium" weight="bold">
                {conditionsLabel}
              </BodyText>
              <MarkdownRender
                content={voucher.offer.conditions}
                fontSize="small"
              />
            </Group>
          )}
        </VoucherDetailsSection>
        <FooterSection>
          <BodyText variant="medium" weight="bold">
            <FormattedMessage
              id="AE3v4Zm78Um1yMF0w8oh"
              defaultMessage="Need help? Call our support team"
              description="Heading for help pane component in the customer support section"
            />
          </BodyText>
          <BodyText variant="small" colour="light-foreground-alt">
            <FormattedMessage
              id="ZcQBSJtId5EsH-9oVrPX"
              defaultMessage="Our customer service team are available anytime, 24 hours, 7 days a week"
              description="Default description text for help pane component in the experiences section"
            />
          </BodyText>
          <PhoneButton href={`tel:${supportContact.local.number}`}>
            <LinePhoneIcon />
            {supportContact.local.humanReadable}
          </PhoneButton>
        </FooterSection>
      </VoucherCardContent>
    </VoucherCardContainer>
  )
}
